import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import ProductModal from '../components/ProductModal';
import { formatINR } from '../utils/priceFormatter';

const Cart = () => {
  const { cartItems, updateQuantity, removeFromCart } = useCart();
  const navigate = useNavigate();
  
  const [promoCode, setPromoCode] = useState('');
  const [isPromoApplied, setIsPromoApplied] = useState(false);
  const [shippingMethod, setShippingMethod] = useState('standard');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  const containerRef = useRef();
  const itemsRef = useRef();
  const summaryRef = useRef();

  useEffect(() => {
    // Entrance animations
    const tl = gsap.timeline();
    
    tl.fromTo(itemsRef.current,
      { x: -50, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.8, ease: "power3.out" }
    )
    .fromTo(summaryRef.current,
      { x: 50, opacity: 0 },
      { x: 0, opacity: 1, duration: 0.8, ease: "power3.out" },
      "-=0.6"
    );
  }, []);

  const handleUpdateQuantity = (id, size, color, newQuantity) => {
    updateQuantity(id, size, color, newQuantity);
  };

  const handleRemoveItem = (id, size, color) => {
    removeFromCart(id, size, color);
  };

  const applyPromoCode = () => {
    if (promoCode.toLowerCase() === 'nike20') {
      setIsPromoApplied(true);
    } else {
      alert('Invalid promo code');
    }
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const calculateDiscount = () => {
    return isPromoApplied ? calculateSubtotal() * 0.2 : 0;
  };

  const calculateShipping = () => {
    const subtotal = calculateSubtotal();
    if (subtotal >= 8300) return 0; // Free shipping over ₹8,300 (equivalent to $100)
    
    switch (shippingMethod) {
      case 'express': return 1245; // ₹1,245 (15 USD * 83)
      case 'overnight': return 2075; // ₹2,075 (25 USD * 83)
      default: return 664; // ₹664 (8 USD * 83) standard
    }
  };

  const calculateTax = () => {
    const subtotalAfterDiscount = calculateSubtotal() - calculateDiscount();
    return subtotalAfterDiscount * 0.08; // 8% tax
  };

  const calculateTotal = () => {
    return calculateSubtotal() - calculateDiscount() + calculateShipping() + calculateTax();
  };

  const handleProductClick = (item) => {
    // Convert cart item to product format for modal
    const productForModal = {
      id: item.id,
      name: item.name,
      price: item.price,
      image: item.image,
      category: item.category || 'Footwear',
      description: item.description || 'Premium Nike product with exceptional quality and comfort.',
      sizes: ['US 7', 'US 8', 'US 9', 'US 10', 'US 11', 'US 12'],
      colors: ['Black', 'White', 'Red', 'Blue'],
      rating: item.rating || 4.5,
      reviews: item.reviews || 128,
      features: item.features || [
        'Premium materials',
        'Comfortable fit',
        'Durable construction',
        'Modern design'
      ]
    };
    
    setSelectedProduct(productForModal);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
  };

  if (cartItems.length === 0) {
    return (
      <EmptyCartContainer>
        <EmptyCartContent>
          <EmptyCartIcon>🛒</EmptyCartIcon>
          <EmptyCartTitle>Your cart is empty</EmptyCartTitle>
          <EmptyCartSubtitle>Add some Nike products to get started</EmptyCartSubtitle>
          <ShopNowButton as={Link} to="/store">
            Continue Shopping
          </ShopNowButton>
        </EmptyCartContent>
      </EmptyCartContainer>
    );
  }

  return (
    <CartContainer ref={containerRef}>
      <CartContent>
        <CartHeader>
          <CartTitle>Shopping Cart</CartTitle>
          <ItemCount>{cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}</ItemCount>
        </CartHeader>

        <CartMain>
          <CartItems ref={itemsRef}>
            {cartItems.map(item => (
              <CartItem key={`${item.id}-${item.size}-${item.color}`}>
                <ProductInfo onClick={() => handleProductClick(item)}>
                  <ProductImage>
                    <img src={item.image} alt={item.name} />
                  </ProductImage>
                  <ProductDetails>
                    <ProductName>{item.name}</ProductName>
                    <ProductMeta>{item.size} • {item.color}</ProductMeta>
                  </ProductDetails>
                </ProductInfo>

                <PriceActions>
                  <UnitPrice>{formatINR(item.price)}</UnitPrice>
                  <QuantityControls>
                    <QuantityButton 
                      onClick={() => handleUpdateQuantity(item.id, item.size, item.color, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >
                      −
                    </QuantityButton>
                    <QuantityInput 
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleUpdateQuantity(item.id, item.size, item.color, parseInt(e.target.value) || 1)}
                    />
                    <QuantityButton 
                      onClick={() => handleUpdateQuantity(item.id, item.size, item.color, item.quantity + 1)}
                    >
                      +
                    </QuantityButton>
                  </QuantityControls>
                  <ItemTotal>{formatINR(item.price * item.quantity)}</ItemTotal>
                </PriceActions>

                <RemoveButton onClick={() => handleRemoveItem(item.id, item.size, item.color)}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="m19 7-.867 12.142A2 2 0 0 1 16.138 21H7.862a2 2 0 0 1-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v3M4 7h16"/>
                  </svg>
                </RemoveButton>
              </CartItem>
            ))}
          </CartItems>

          <CartSummary ref={summaryRef}>
            <SummaryHeader>
              <SummaryTitle>Order Summary</SummaryTitle>
            </SummaryHeader>

            <PromoSection>
              <PromoTitle>Promo Code</PromoTitle>
              <PromoInput>
                <input
                  type="text"
                  placeholder="Enter code"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                />
                <PromoButton onClick={applyPromoCode}>Apply</PromoButton>
              </PromoInput>
              {isPromoApplied && (
                <PromoSuccess>✓ NIKE20 applied - 20% off!</PromoSuccess>
              )}
            </PromoSection>

            <ShippingSection>
              <ShippingTitle>Shipping Options</ShippingTitle>
              <ShippingOptions>
                <ShippingOption>
                  <input
                    type="radio"
                    id="standard"
                    name="shipping"
                    value="standard"
                    checked={shippingMethod === 'standard'}
                    onChange={(e) => setShippingMethod(e.target.value)}
                  />
                  <label htmlFor="standard">
                    <ShippingName>Standard Shipping</ShippingName>
                    <ShippingTime>5-7 business days</ShippingTime>
                  </label>
                  <ShippingPrice>₹664</ShippingPrice>
                </ShippingOption>
                
                <ShippingOption>
                  <input
                    type="radio"
                    id="express"
                    name="shipping"
                    value="express"
                    checked={shippingMethod === 'express'}
                    onChange={(e) => setShippingMethod(e.target.value)}
                  />
                  <label htmlFor="express">
                    <ShippingName>Express Shipping</ShippingName>
                    <ShippingTime>2-3 business days</ShippingTime>
                  </label>
                  <ShippingPrice>₹1,245</ShippingPrice>
                </ShippingOption>
                
                <ShippingOption>
                  <input
                    type="radio"
                    id="overnight"
                    name="shipping"
                    value="overnight"
                    checked={shippingMethod === 'overnight'}
                    onChange={(e) => setShippingMethod(e.target.value)}
                  />
                  <label htmlFor="overnight">
                    <ShippingName>Overnight Shipping</ShippingName>
                    <ShippingTime>Next business day</ShippingTime>
                  </label>
                  <ShippingPrice>₹2,075</ShippingPrice>
                </ShippingOption>
              </ShippingOptions>
            </ShippingSection>

            <SummaryBreakdown>
              <SummaryLine>
                <SummaryLabel>Subtotal</SummaryLabel>
                <SummaryValue>{formatINR(calculateSubtotal())}</SummaryValue>
              </SummaryLine>
              
              {isPromoApplied && (
                <SummaryLine>
                  <SummaryLabel>Discount (20%)</SummaryLabel>
                  <SummaryValue discount>-{formatINR(calculateDiscount())}</SummaryValue>
                </SummaryLine>
              )}
              
              <SummaryLine>
                <SummaryLabel>Shipping</SummaryLabel>
                <SummaryValue>
                  {calculateShipping() === 0 ? 'FREE' : formatINR(calculateShipping())}
                </SummaryValue>
              </SummaryLine>
              
              <SummaryLine>
                <SummaryLabel>Tax</SummaryLabel>
                <SummaryValue>{formatINR(calculateTax())}</SummaryValue>
              </SummaryLine>
              
              <SummaryDivider />
              
              <SummaryLine total>
                <SummaryLabel>Total</SummaryLabel>
                <SummaryValue>{formatINR(calculateTotal())}</SummaryValue>
              </SummaryLine>
            </SummaryBreakdown>

            <CheckoutActions>
              <CheckoutButton onClick={() => navigate('/checkout')}>
                Proceed to Checkout
              </CheckoutButton>
              <ContinueShoppingLink as={Link} to="/store">
                Continue Shopping
              </ContinueShoppingLink>
            </CheckoutActions>

            <SecurityBadges>
              <SecurityBadge>🔒 Secure Checkout</SecurityBadge>
              <SecurityBadge>📦 Free Returns</SecurityBadge>
              <SecurityBadge>⚡ Fast Shipping</SecurityBadge>
            </SecurityBadges>
          </CartSummary>
        </CartMain>
      </CartContent>

      {showModal && selectedProduct && (
        <ProductModal 
          isOpen={showModal}
          product={selectedProduct}
          onClose={handleCloseModal}
        />
      )}
    </CartContainer>
  );
};

export default Cart;

// Styled Components
const CartContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 20px 0;
`;

const CartContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 24px;
  
  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const CartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
`;

const CartTitle = styled.h1`
  font-family: 'Anton', sans-serif;
  font-size: clamp(1.8rem, 3vw, 2.5rem);
  color: #111;
  margin: 0;
  letter-spacing: 1px;
`;

const ItemCount = styled.span`
  font-size: 16px;
  color: #666;
  font-weight: 500;
`;

const CartMain = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 40px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const CartItems = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const CartItem = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
  position: relative;
  transition: background-color 0.2s ease;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: #f9fafb;
  }
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const ProductInfo = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex: 1;
  min-width: 0;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 8px;
  margin: -8px;
  border-radius: 8px;
  position: relative;
  
  &:hover {
    background: linear-gradient(145deg, rgba(59, 130, 246, 0.05), rgba(147, 197, 253, 0.03));
    transform: translateX(4px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
    border-left: 3px solid #3b82f6;
    padding-left: 5px;
  }
  
  &:active {
    transform: translateX(2px);
  }
  
  @media (max-width: 768px) {
    gap: 12px;
    flex-basis: 100%;
    order: 1;
    padding: 6px;
    margin: -6px;
    
    &:hover {
      transform: none;
      background: rgba(59, 130, 246, 0.08);
      border-left: 2px solid #3b82f6;
      padding-left: 4px;
      box-shadow: 0 1px 4px rgba(59, 130, 246, 0.15);
    }
  }
`;

const ProductImage = styled.div`
  width: 60px;
  height: 60px;
  background: #f8f9fa;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid #e5e7eb;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    border-radius: 4px;
  }
`;

const ProductDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProductName = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 2px 0;
  line-height: 1.4;
  transition: color 0.2s ease;
  
  ${ProductInfo}:hover & {
    color: #2563eb;
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    margin: 0 0 1px 0;
  }
`;

const ProductMeta = styled.div`
  font-size: 13px;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
  
  &::after {
    content: "View Details";
    position: absolute;
    right: -100px;
    top: 50%;
    transform: translateY(-50%);
    background: #1f2937;
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    opacity: 0;
    pointer-events: none;
    transition: all 0.3s ease;
    white-space: nowrap;
    z-index: 10;
  }
  
  &::before {
    content: "";
    position: absolute;
    right: -92px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 4px solid transparent;
    border-bottom: 4px solid transparent;
    border-right: 4px solid #1f2937;
    opacity: 0;
    transition: all 0.3s ease;
    z-index: 10;
  }
  
  ${ProductInfo}:hover &::after {
    opacity: 1;
    right: -110px;
  }
  
  ${ProductInfo}:hover &::before {
    opacity: 1;
    right: -102px;
  }
  
  @media (max-width: 768px) {
    font-size: 12px;
    
    &::after,
    &::before {
      display: none;
    }
  }
`;

const PriceActions = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-basis: 100%;
    justify-content: space-between;
    order: 2;
    margin-top: 8px;
    gap: 12px;
  }
`;

const UnitPrice = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  min-width: 60px;
  text-align: right;
  
  @media (max-width: 768px) {
    font-size: 13px;
    min-width: auto;
    text-align: left;
  }
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
`;

const QuantityButton = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #e5e7eb;
    color: #374151;
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const QuantityInput = styled.input`
  width: 32px;
  height: 28px;
  text-align: center;
  border: none;
  background: white;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  
  &:focus {
    outline: none;
    background: #f9fafb;
  }
  
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &[type=number] {
    -moz-appearance: textfield;
  }
`;

const ItemTotal = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  min-width: 80px;
  text-align: right;
  
  @media (max-width: 768px) {
    font-size: 15px;
    min-width: auto;
    text-align: left;
  }
`;

const RemoveButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #9ca3af;
  transition: all 0.2s ease;
  margin-left: 8px;
  
  &:hover {
    background: #fef2f2;
    color: #dc2626;
  }
  
  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
    margin-left: 4px;
  }
`;

const CartSummary = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.06);
  height: fit-content;
  position: sticky;
  top: 100px;
  
  @media (max-width: 1024px) {
    position: static;
  }
`;

const SummaryHeader = styled.div`
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
`;

const SummaryTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #111;
  margin: 0;
`;

const PromoSection = styled.div`
  margin-bottom: 24px;
`;

const PromoTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111;
  margin: 0 0 12px 0;
`;

const PromoInput = styled.div`
  display: flex;
  gap: 8px;
  
  input {
    flex: 1;
    padding: 12px 16px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    font-size: 14px;
    
    &:focus {
      outline: none;
      border-color: #111;
    }
  }
`;

const PromoButton = styled.button`
  padding: 12px 20px;
  background: #111;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #333;
  }
`;

const PromoSuccess = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background: #d4edda;
  color: #155724;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
`;

const ShippingSection = styled.div`
  margin-bottom: 24px;
`;

const ShippingTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111;
  margin: 0 0 12px 0;
`;

const ShippingOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ShippingOption = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #111;
    background: #f8f9fa;
  }
  
  input[type="radio"] {
    accent-color: #111;
  }
  
  label {
    flex: 1;
    cursor: pointer;
  }
`;

const ShippingName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #111;
`;

const ShippingTime = styled.div`
  font-size: 12px;
  color: #666;
`;

const ShippingPrice = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111;
`;

const SummaryBreakdown = styled.div`
  margin-bottom: 24px;
`;

const SummaryLine = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  
  ${props => props.total && `
    font-size: 18px;
    font-weight: 600;
    padding-top: 12px;
  `}
`;

const SummaryLabel = styled.span`
  font-size: 14px;
  color: #666;
`;

const SummaryValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #111;
  
  ${props => props.discount && `
    color: #27ae60;
  `}
`;

const SummaryDivider = styled.div`
  height: 1px;
  background: #e0e0e0;
  margin: 16px 0;
`;

const CheckoutActions = styled.div`
  margin-bottom: 20px;
`;

const CheckoutButton = styled.button`
  width: 100%;
  background: #111;
  color: white;
  border: none;
  padding: 16px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 12px;
  
  &:hover {
    background: #000;
    transform: translateY(-1px);
  }
`;

const ContinueShoppingLink = styled.a`
  display: block;
  text-align: center;
  color: #111;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SecurityBadges = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const SecurityBadge = styled.div`
  font-size: 12px;
  color: #666;
  text-align: center;
  flex: 1;
`;

// Empty Cart Styles
const EmptyCartContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
`;

const EmptyCartContent = styled.div`
  text-align: center;
  max-width: 400px;
  padding: 40px 20px;
`;

const EmptyCartIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
`;

const EmptyCartTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #111;
  margin: 0 0 12px 0;
`;

const EmptyCartSubtitle = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0 0 30px 0;
`;

const ShopNowButton = styled.button`
  background: #111;
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  display: inline-block;
  
  &:hover {
    background: #000;
    transform: translateY(-1px);
  }
`;